import { useLayoutEffect, useRef, useState } from 'react'
import type { AgentV2MessageAttachedFiles } from '@/agent-v2/store/reducer'
import { TurnAttachedFilesDropdown, TurnAttachedFilesDropdownTrigger, type TurnAttachedFileChip } from '@/agent-v2/components/TurnAttachedFilesDropdown'
import { getVisibleChipCount } from '@/agent-v2/utils/get-visible-chip-count'
import { useOpenChatAttachedFile } from '@/agent-v2/hooks/useOpenChatAttachedFile'
import { FileChip } from '@/components/file-chip/FileChip'
import { useUserFiles } from '@/user_files/files/hooks/useUserFiles'
import { canOpenAttachedFile, kDeletedFileName } from '@/agent-v2/utils/chat-attached-file'

const kMaxRows = 2
// Matches the row's gap-2
const kChipGapPx = 8

/** The read-only chips above a user message for the files that turn attached, up to two rows with the rest in a menu. */
export function TurnAttachedFiles({ conversationId, attachedFiles }: { conversationId: string; attachedFiles: AgentV2MessageAttachedFiles }) {
  const { fileIds, writingSampleFileId } = attachedFiles
  const { filesById, unavailableIds } = useUserFiles(writingSampleFileId ? [...fileIds, writingSampleFileId] : fileIds)
  const openChatAttachedFile = useOpenChatAttachedFile(conversationId)

  // A file shows by name once its record loads, or as a deleted file once a fetch has come back without it
  const toChip = (fileId: string, isWritingSample: boolean): TurnAttachedFileChip | null => {
    const key = `${isWritingSample ? 'writing-sample' : 'file'}:${fileId}`
    const file = filesById[fileId]
    if (!file) return unavailableIds.includes(fileId) ? { key, name: kDeletedFileName, isWritingSample, disabled: true } : null

    return {
      key,
      name: file.original_filename,
      isWritingSample,
      disabled: false,
      onOpen: canOpenAttachedFile(file) ? () => openChatAttachedFile(file) : undefined,
    }
  }
  const chips = [writingSampleFileId ? toChip(writingSampleFileId, true) : null, ...fileIds.map((fileId) => toChip(fileId, false))].filter(
    (chip): chip is TurnAttachedFileChip => chip !== null
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(chips.length)
  const chipsKey = chips.map((chip) => `${chip.key}:${chip.name}`).join('|')

  // Measures every chip at its natural width in the hidden copy, then fits as many as two rows of the container hold
  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const update = () => {
      const widths = [...measure.children].map((child) => child.getBoundingClientRect().width)
      const triggerWidth = widths.pop() ?? 0
      setVisibleCount(getVisibleChipCount({ chipWidths: widths, triggerWidth, containerWidth: container.clientWidth, gap: kChipGapPx, maxRows: kMaxRows }))
    }
    update()

    const observer = new ResizeObserver(update)
    observer.observe(container)
    observer.observe(measure)
    return () => observer.disconnect()
  }, [chipsKey])

  if (chips.length === 0) return null

  // A long name truncates at the 320px cap, with the full name in its hover label
  const renderChip = (chip: TurnAttachedFileChip) => (
    <div key={chip.key} className="max-w-[320px]">
      <FileChip name={chip.name} variant={chip.isWritingSample ? 'template' : 'file'} disabled={chip.disabled} onClick={chip.onOpen} />
    </div>
  )
  const overflowChips = chips.slice(visibleCount)

  return (
    <div className="relative w-full">
      <div ref={containerRef} className="flex flex-wrap justify-end gap-2">
        {chips.slice(0, visibleCount).map(renderChip)}
        {overflowChips.length > 0 && <TurnAttachedFilesDropdown chips={overflowChips} />}
      </div>
      {/* Clipped to nothing, so the full-width copy never widens the page */}
      <div aria-hidden className="invisible absolute left-0 top-0 size-0 overflow-hidden">
        <div ref={measureRef} className="flex w-max gap-2">
          {chips.map(renderChip)}
          <TurnAttachedFilesDropdownTrigger count={chips.length} />
        </div>
      </div>
    </div>
  )
}
